import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useDeleteExportSalesTracker, useDeleteExportSalesTrackerContract } from "@/lib/api/exportSalesTracking";
import { AlertTriangle } from "lucide-react";

interface DeleteConfirmDialogProps {
  type: "tracker" | "contract";
  trackerId: string;
  contractId?: string;
  settlementNumber?: string;
  contractNumber?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function DeleteConfirmDialog({
  type,
  trackerId,
  contractId,
  settlementNumber,
  contractNumber,
  open,
  onOpenChange,
  onSuccess
}: DeleteConfirmDialogProps) {
  const { toast } = useToast();
  const deleteTracker = useDeleteExportSalesTracker();
  const deleteContract = useDeleteExportSalesTrackerContract();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);

    try {
      let result;
      
      if (type === "tracker") {
        result = await deleteTracker(trackerId);
      } else if (type === "contract" && contractId) {
        result = await deleteContract(trackerId, contractId);
      } else {
        throw new Error("Invalid delete operation");
      }

      // Check if result is undefined (successful deletion)
      if (result === undefined || (result && result.success !== false)) {
        toast({
          title: "Success",
          description: type === "tracker" 
            ? `Settlement ${settlementNumber || ""} deleted successfully`
            : `Contract ${contractNumber || ""} deleted successfully`
        });
        onSuccess?.();
        onOpenChange(false);
      } else {
        toast({
          title: "Error",
          description: result?.message || `Failed to delete ${type}`,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while deleting",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getDialogTitle = () => {
    if (type === "tracker") {
      return settlementNumber ? `Delete Settlement ${settlementNumber}` : "Delete Settlement";
    }
    return contractNumber ? `Delete Contract ${contractNumber}` : "Delete Contract";
  };

  const getDialogDescription = () => {
    if (type === "tracker") {
      return "This will delete the entire settlement and all associated contracts. This action cannot be undone.";
    }
    return "This will permanently delete this contract. This action cannot be undone.";
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open && !isDeleting) {
        onOpenChange(false);
      }
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}